import { OFFERED_PERMISSIONS, analyticsGateSatisfied } from "@colossus/contracts";
import type { PermissionKey, PresetKey } from "@colossus/contracts";
import { m } from "./paraglide/messages.js";
import type { Message } from "./locale.ts";
import type { ViewEntry } from "./view-index.ts";

export const TEAM_ACCESS_READ = "team.access.read";
export const TEAM_ACCESS_WRITE = "team.access.write";
export {
  BILLING_READ as TEAM_BILLING_READ,
  SETTINGS_DATA_READ as TEAM_SETTINGS_DATA_READ,
  SETTINGS_DATA_WRITE as TEAM_SETTINGS_DATA_WRITE,
  SETTINGS_SERVER_READ as TEAM_SETTINGS_SERVER_READ,
  SETTINGS_SERVER_WRITE as TEAM_SETTINGS_SERVER_WRITE,
  VIEWS_SHARE as TEAM_VIEWS_SHARE,
} from "@colossus/contracts";
export const TEAM_CREDENTIALS_WRITE = "team.credentials.write";
export {
  BILLING_WRITE as TEAM_BILLING_WRITE,
  BOT_WRITE as TEAM_BOT_WRITE,
  LIFECYCLE_DELETE as TEAM_LIFECYCLE_DELETE,
  SETTINGS_DATA_PURGE as TEAM_SETTINGS_DATA_PURGE,
} from "@colossus/contracts";
export const TEAM_AUDIT_READ = "team.audit.read";
export const PRODUCT_BACKFILL_READ = "product.backfill.read";
export const PRODUCT_BACKFILL_WRITE = "product.backfill.write";
export const PRODUCT_ANNOTATIONS_WRITE = "product.annotations.write";
export const PRODUCT_PULSE_READ = "product.pulse.read";

export const has = (permissions: readonly string[], key: string): boolean =>
  permissions.includes(key);

export const hasAny = (permissions: readonly string[], keys: readonly string[]): boolean =>
  keys.some((key) => has(permissions, key));

export const PERMISSION_LABEL: Readonly<Record<PermissionKey, Message>> = {
  "team.access.read": m.perm_team_access_read,
  "team.access.write": m.perm_team_access_write,
  "team.settings.read": m.perm_team_settings_read,
  "team.settings.write": m.perm_team_settings_write,
  "team.settings.server.read": m.perm_team_settings_server_read,
  "team.settings.server.write": m.perm_team_settings_server_write,
  "team.settings.data.read": m.perm_team_settings_data_read,
  "team.settings.data.write": m.perm_team_settings_data_write,
  "team.billing.read": m.perm_team_billing_read,
  "team.views.share": m.perm_team_views_share,
  "team.credentials.write": m.perm_team_credentials_write,
  "team.lifecycle.write": m.perm_team_lifecycle_write,
  "team.audit.read": m.perm_team_audit_read,
  "product.analytics.growth.read": m.perm_product_analytics_growth_read,
  "product.analytics.engagement.read": m.perm_product_analytics_engagement_read,
  "product.analytics.audience.read": m.perm_product_analytics_audience_read,
  "product.analytics.profiles.read": m.perm_product_analytics_profiles_read,
  "product.audit.read": m.perm_product_audit_read,
  "product.backfill.read": m.perm_product_backfill_read,
  "product.backfill.write": m.perm_product_backfill_write,
  "product.annotations.write": m.perm_product_annotations_write,
  "product.pulse.read": m.perm_product_pulse_read,
  "product.leveling.write": m.perm_product_leveling_write,
  "team.billing.write": m.perm_team_billing_write,
  "team.bot.write": m.perm_team_bot_write,
  "team.lifecycle.delete": m.perm_team_lifecycle_delete,
  "team.settings.data.purge": m.perm_team_settings_data_purge,
  "product.leveling.read": m.perm_product_leveling_read,
  "product.leveling.config.write": m.perm_product_leveling_config_write,
  "product.leveling.imports.read": m.perm_product_leveling_imports_read,
  "product.leveling.imports.write": m.perm_product_leveling_imports_write,
  "product.leveling.adjust.write": m.perm_product_leveling_adjust_write,
  "product.leveling.claims.read": m.perm_product_leveling_claims_read,
  "product.leveling.claims.write": m.perm_product_leveling_claims_write,
  "product.leveling.pictures.read": m.perm_product_leveling_pictures_read,
  "product.leveling.pictures.write": m.perm_product_leveling_pictures_write,
  "product.leveling.member-roles.read": m.perm_product_leveling_member_roles_read,
  "product.leveling.member-roles.write": m.perm_product_leveling_member_roles_write,
  "product.images.read": m.perm_product_images_read,
  "product.images.write": m.perm_product_images_write,
  "product.giveaways.read": m.perm_product_giveaways_read,
  "product.giveaways.write": m.perm_product_giveaways_write,
};

const SETTINGS_PREFIX = "team.settings.";

export const permissionDomains = (
  _grants: readonly string[],
): readonly {
  readonly id: "product" | "settings" | "team";
  readonly title: Message;
  readonly keys: readonly PermissionKey[];
}[] => {
  const listed = OFFERED_PERMISSIONS;
  return [
    {
      id: "product",
      title: m.perm_domain_product,
      keys: listed.filter((key) => key.startsWith("product.")),
    },
    {
      id: "settings",
      title: m.perm_domain_settings,
      keys: listed.filter((key) => key.startsWith(SETTINGS_PREFIX)),
    },
    {
      id: "team",
      title: m.perm_domain_team,
      keys: listed.filter((key) => key.startsWith("team.") && !key.startsWith(SETTINGS_PREFIX)),
    },
  ];
};

export const PRESET_LABEL: Readonly<Record<PresetKey, Message>> = {
  viewer: m.preset_viewer,
  manager: m.preset_manager,
  admin: m.preset_admin,
};

export const PRESET_HINT: Readonly<Record<PresetKey, Message>> = {
  viewer: m.preset_viewer_hint,
  manager: m.preset_manager_hint,
  admin: m.preset_admin_hint,
};

export const viewAllowed = (
  view: Pick<ViewEntry, "gates">,
  permissions: readonly string[],
): boolean => view.gates.every((gate) => analyticsGateSatisfied(gate, permissions));

export { analyticsReadAllowed as panelAllowed } from "@colossus/contracts";
